// src/types/common.ts
// Pure types and interfaces - NO LOGIC HERE

export type ID = string | number;
export type Locale = 'vi' | 'en';

/**
 * Base model interface for all entities
 */
export interface BaseModel {
    id: ID;
    createdAt: string;
    updatedAt: string;
    publishedAt?: string;
    documentId?: string; // Strapi v5
}

/**
 * Localized model with multi-language support
 */
export interface LocalizedModel<T extends BaseModel> {
    locale: Locale;
    localizations?: {
        data: Array<{
            id: ID;
            attributes: T & { locale: Locale };
        }>;
    };
}

/**
 * Pagination metadata structure
 */
export interface PaginationMeta {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
}

/**
 * Generic API response structure
 */
export interface ApiResponse<T> {
    data: T;
    meta: {
        pagination?: PaginationMeta;
        [key: string]: any;
    };
}

/**
 * Strapi v5 specific response structures
 */
export interface StrapiSingleItem<T> {
    id: ID;
    documentId: string;
    attributes: T;
}

export interface StrapiMultipleItems<T> {
    id: ID;
    documentId: string;
    attributes: T;
}

export interface StrapiSingleResponse<T> {
    data: StrapiSingleItem<T> | null;
    meta: Record<string, any>;
}

export interface StrapiMultipleResponse<T> {
    data: StrapiMultipleItems<T>[];
    meta: {
        pagination?: PaginationMeta;
        [key: string]: any;
    };
}

export interface StrapiResponse<T> {
    data: T;
    meta: {
        pagination?: PaginationMeta;
        [key: string]: any;
    };
}

/**
 * Media file structure from Strapi
 */
export interface StrapiMedia {
    id: ID;
    documentId?: string;
    name: string;
    alternativeText?: string;
    caption?: string;
    width?: number;
    height?: number;
    formats?: {
        thumbnail?: MediaFormat;
        small?: MediaFormat;
        medium?: MediaFormat;
        large?: MediaFormat;
    };
    hash: string;
    ext: string;
    mime: string;
    size: number;
    url: string;
    previewUrl?: string;
    provider: string;
    provider_metadata?: Record<string, any>;
    createdAt: string;
    updatedAt: string;
}

export interface MediaFormat {
    url: string;
    width: number;
    height: number;
    size?: number;
}

/**
 * Media response structure
 */
export interface StrapiMediaResponse {
    data: Array<{
        id: ID;
        documentId?: string;
        attributes: StrapiMedia;
    }> | {
        id: ID;
        documentId?: string;
        attributes: StrapiMedia;
    } | null;
}

/**
 * Relation structures
 */
export interface StrapiRelationOne<T> {
    data: {
        id: ID;
        documentId?: string;
        attributes: T;
    } | null;
}

export interface StrapiRelationMany<T> {
    data: Array<{
        id: ID;
        documentId?: string;
        attributes: T;
    }>;
}

/**
 * API query parameters
 */
export interface StrapiQueryParams {
    populate?: string | string[] | Record<string, any>;
    filters?: Record<string, any>;
    sort?: string | string[];
    pagination?: {
        page?: number;
        pageSize?: number;
        start?: number;
        limit?: number;
    };
    locale?: Locale;
    fields?: string[];
    publicationState?: 'live' | 'preview';
}

/**
 * Error structures
 */
export interface StrapiError {
    status: number;
    name: string;
    message: string;
    details?: Record<string, any>;
}

export interface ApiError extends Error {
    statusCode?: number;
    code?: string;
    details?: Record<string, any>;
    response?: {
        data?: any;
        status?: number;
        statusText?: string;
    };
}

/**
 * Operation result wrapper
 */
export interface OperationResult<T> {
    success: boolean;
    data?: T;
    error?: ApiError;
    metadata?: {
        operation: string;
        timestamp: string;
        duration?: number;
        recordId?: string | number;
        schema?: string;
    };
}

/**
 * Bulk operation results
 */
export interface BulkOperationResult<T = any> {
    results: Array<{
        id: string | number;
        success: boolean;
        data?: T;
        error?: string;
    }>;
    summary: {
        total: number;
        successful: number;
        failed: number;
    };
}

/**
 * Business domain enums and types
 */

/**
 * Content lifecycle status
 * @description Follows: draft → submitted → under_review → approved/rejected → published
 */
export type ContentStatus =
    | 'draft'             // Nháp, đang soạn thảo
    | 'submitted'         // Đã nộp, chờ xem xét
    | 'pending'           // Đã nộp, chờ xem xét ??
    | 'under_review'      // Đang được xem xét
    | 'approved'          // Đã duyệt, sẵn sàng publish
    | 'rejected'          // Từ chối, cần chỉnh sửa
    | 'revision_required' // Cần chỉnh sửa theo yêu cầu
    | 'published'         // Đã xuất bản, public
    | 'suspended'         // Tạm ngưng hiển thị
    | 'deleted'           // Tạm ngưng hiển thị ??
    | 'archived';         // Lưu trữ, không active

/**
 * Content type classification
 */
export type ContentKind = 'image' | 'text' | 'video' | 'audio' | 'other';

/**
 * License status in system
 */
export type LicenseStatus = 'pending' | 'active' | 'expired' | 'cancelled';

/**
 * Contract lifecycle status
 */
export type ContractStatus = 'draft' | 'pending_approval' | 'active' | 'completed' | 'cancelled';

/**
 * Violation processing status
 * @description Complex workflow with multiple paths
 */
export type ViolationStatus =
    | 'detected'           // Phát hiện vi phạm
    | 'verified'           // Xác minh vi phạm
    | 'notified'           // Đã thông báo
    | 'acknowledged'       // Đã được thừa nhận
    | 'content_removed'    // Nội dung đã được gỡ
    | 'content_modified'   // Nội dung đã được sửa đổi
    | 'dispute_filed'      // Đã khiếu nại
    | 'under_review'       // Đang xem xét khiếu nại
    | 'resolved'           // Đã giải quyết
    | 'dismissed'          // Bác bỏ
    | 'legal_action'       // Hành động pháp lý
    | 'escalated'          // Leo thang
    | 'expired';           // Hết hạn xử lý

/**
 * Violation type classification
 */
export type ViolationType =
    | "copyright_infringement"
    | "trademark_violation"
    | "unauthorized_usage"
    | "plagiarism"
    | "deep_fake"
    | "content_theft"
    | "license_violation"
    | "impersonation"
    | "contract_violation"
    | "other";

/**
 * Payment processing status
 */
export type PaymentStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'refunded' | 'partially_refunded' | 'cancelled';

/**
 * Document processing status
 */
export type DocumentStatus = 'draft' | 'pendingReview' | 'published' | 'rejected' | 'deleted';

/**
 * Security and fingerprinting
 */
export interface FingerprintDetails {
    visitorId: string;         // Unique ID from FingerprintJS
    ip?: string;               // IP address (may be null)
    userAgent?: string;        // Browser user agent string
    language?: string;         // Browser language
    platform?: string;        // Operating system
    deviceType?: string;       // Device type
    browserName?: string;      // Browser name
    browserVersion?: string;   // Browser version
    screenResolution?: string; // Screen resolution
    colorDepth?: number;       // Color depth
    timezoneOffset?: number;   // Timezone offset in minutes from UTC
}

/**
 * System constants
 */
export const STRAPI_CONSTANTS = {
    // Strapi v5 documentId validation
    DOCUMENT_ID_PATTERN: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    MIN_DOCUMENT_ID_LENGTH: 36,
    MAX_DOCUMENT_ID_LENGTH: 36,

    // API operation limits
    MAX_BULK_OPERATION_SIZE: 50,
    DEFAULT_PAGE_SIZE: 25,
    MAX_PAGE_SIZE: 100,

    // Retry configuration
    DEFAULT_RETRY_ATTEMPTS: 3,
    DEFAULT_RETRY_DELAY: 1000,
    MAX_RETRY_DELAY: 5000,

    // Cache TTL (in milliseconds)
    DEFAULT_CACHE_TTL: 5 * 60 * 1000, // 5 minutes
    LONG_CACHE_TTL: 60 * 60 * 1000,   // 1 hour

    // HTTP status codes
    HTTP_STATUS: {
        OK: 200,
        CREATED: 201,
        NO_CONTENT: 204,
        BAD_REQUEST: 400,
        UNAUTHORIZED: 401,
        FORBIDDEN: 403,
        NOT_FOUND: 404,
        CONFLICT: 409,
        UNPROCESSABLE_ENTITY: 422,
        INTERNAL_SERVER_ERROR: 500
    }
} as const;

/**
 * Content status groupings for business logic
 */
export const CONTENT_STATUS_GROUPS = {
    DRAFT: ['draft'] as const,
    PENDING: ['submitted', 'under_review'] as const,
    NEEDS_ACTION: ['rejected', 'revision_required'] as const,
    READY: ['approved'] as const,
    LIVE: ['published'] as const,
    INACTIVE: ['suspended', 'archived'] as const
} as const;

/**
 * Violation status groupings for workflow management
 */
export const VIOLATION_STATUS_GROUPS = {
    INITIAL: ['detected', 'verified'] as const,
    PROCESSING: ['notified', 'acknowledged', 'under_review'] as const,
    RESOLVED: ['content_removed', 'content_modified', 'resolved'] as const,
    ESCALATED: ['dispute_filed', 'legal_action', 'escalated'] as const,
    CLOSED: ['dismissed', 'expired'] as const
} as const;

/**
 * Type guards
 */
export const isDocumentIdFormat = (id: string | number): id is string => {
    return typeof id === 'string' && STRAPI_CONSTANTS.DOCUMENT_ID_PATTERN.test(id);
};

export const isValidContentStatus = (status: string): status is ContentStatus => {
    const validStatuses: ContentStatus[] = [
        'draft', 'submitted', 'under_review', 'approved', 'rejected',
        'revision_required', 'published', 'suspended', 'archived'
    ];
    return validStatuses.includes(status as ContentStatus);
};

export const isValidViolationStatus = (status: string): status is ViolationStatus => {
    const validStatuses: ViolationStatus[] = [
        'detected', 'verified', 'notified', 'acknowledged', 'content_removed',
        'content_modified', 'dispute_filed', 'under_review', 'resolved',
        'dismissed', 'legal_action', 'escalated', 'expired'
    ];
    return validStatuses.includes(status as ViolationStatus);
};

/**
 * Utility types for advanced TypeScript patterns
 */
export type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

export type OptionalFields<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type ExtractArrayType<T> = T extends (infer U)[] ? U : never;
